import { useRouter } from "next/router"
import { type FC, useCallback, useContext, useEffect, useRef, useState } from "react"

import {
  type BaseCommunityFragment,
  type BaseUserFragment,
  UserInfoField,
  useJoinCommunityMutation,
} from "../generated/graphql"
import { graphQLClient } from "../graphql/client"
import { UserContext } from "../providers/UserProvider"
import { getGraphQLErrorMessage, processException } from "../utils/errors"
import { normalizeInviteCode } from "../utils/inviteCode"
import { useCommunityCustomization } from "../utils/useCommunityCustomization"
import { useCommunityFromCode } from "../utils/useCommunityFromCode"
import { resolveJoinPurchasePath } from "../utils/useRequestCheckout"
import { Auth } from "./gate/Auth"
import { CompleteAccount } from "./gate/CompleteAccount"
import { Landing } from "./gate/Landing"
import { Requirements } from "./gate/Requirements"
import { Submitted } from "./gate/Submitted"
import type { JoinRequirementFieldConfig } from "./JoinRequirementsFields"

type Step = "gate" | "auth" | "account" | "requirements" | "submitted"

const GATE_MODAL_VIEWPORT_STYLES = "overflow-y-auto overscroll-contain"
const GATE_MODAL_CONTAINER_STYLES = "items-start pt-[calc(env(safe-area-inset-top)+63px)] pb-6"
const GATE_MODAL_PANEL_STYLES = ""
// The API's answer when no community has the code, e.g. a personal invite link someone already used.
const INVITE_CODE_NOT_FOUND = "Can't find the community to join"

export const CommunityMemberGateModal: FC<{
  isOpen: boolean
}> = ({ isOpen }) => {
  const router = useRouter()
  const inviteCode = new URLSearchParams(router.asPath.split("?")[1]).get("invite")
  const customizationData = useCommunityCustomization()
  const { community } = useCommunityFromCode()
  const { user, setCommunities, setUser, setAccessToken, refreshUser, logout } =
    useContext(UserContext)

  // Mutations
  const { mutateAsync: joinCommunity } = useJoinCommunityMutation(graphQLClient)

  // Step & general state
  const communityCode = customizationData?.code
  const hasAlreadyRequested = !!user?.communitiesWhereRequested?.some(
    (c) => c.code === communityCode,
  )
  const [step, setStep] = useState<Step>(hasAlreadyRequested ? "submitted" : "gate")
  const [requiredFields, setRequiredFields] = useState<UserInfoField[]>([])
  const [joinRequirementsConfig, setJoinRequirementsConfig] = useState<{
    fields: JoinRequirementFieldConfig[]
  } | null>(null)
  const [communityToJoin, setCommunityToJoin] = useState<BaseCommunityFragment | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Defer rendering to avoid HeadlessUI Portal hydration mismatch
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])

  useEffect(() => {
    if (hasAlreadyRequested && step === "gate") {
      setStep("submitted")
    }
  }, [hasAlreadyRequested, step])

  // A personal invite link joins one person. Once it's used, the community's own code takes over.
  const linkCodeUsedRef = useRef(false)

  const getInviteCode = useCallback(() => {
    const linkCode = linkCodeUsedRef.current ? null : normalizeInviteCode(inviteCode)
    return linkCode ?? normalizeInviteCode(customizationData?.inviteCode)
  }, [inviteCode, customizationData?.inviteCode])

  const performJoinCheck = useCallback(
    async (responses?: Record<string, unknown>) => {
      setIsLoading(true)
      setError(null)
      try {
        let code = getInviteCode()
        if (!code) {
          setError("This community is not accepting new members yet.")
          return
        }
        const join = (codeToTry: string) =>
          joinCommunity({
            inviteCode: codeToTry,
            doInfoChecks: true,
            joinRequirementsResponses: responses ?? undefined,
          })
        let result: Awaited<ReturnType<typeof join>>
        try {
          result = await join(code)
        } catch (err) {
          const communityCode = normalizeInviteCode(customizationData?.inviteCode)
          const linkCodeFailed =
            getGraphQLErrorMessage(err) === INVITE_CODE_NOT_FOUND &&
            !!communityCode &&
            code !== communityCode
          if (!linkCodeFailed) throw err
          linkCodeUsedRef.current = true
          code = communityCode
          result = await join(code)
        }

        const data = result.joinCommunityWithInfoCheck

        if (data?.requestToJoinSubmitted) {
          if (data.communityToJoin) {
            setCommunityToJoin(data.communityToJoin as BaseCommunityFragment)
          }
          setStep("submitted")
          return
        }

        if (data?.communities) {
          setCommunities(data.communities as BaseCommunityFragment[])
          return
        }

        if (data?.missingInfo && data.missingInfo.length > 0) {
          const missing = data.missingInfo.filter((f): f is UserInfoField => f !== null)
          const communityRequiredFields = (
            (data.communityToJoin as BaseCommunityFragment | null)?.requiredUserFields ?? []
          ).filter((f): f is UserInfoField => f !== null)
          const resolvedRequiredFields =
            communityRequiredFields.length > 0 ? communityRequiredFields : missing
          const supportedAccountFields = resolvedRequiredFields.filter((field) =>
            [
              UserInfoField.FirstName,
              UserInfoField.LastName,
              UserInfoField.Username,
              UserInfoField.Email,
              UserInfoField.Dob,
            ].includes(field),
          )

          if (supportedAccountFields.length === 0) {
            // If there are no renderable account fields, skip account step and continue flow.
            const fallbackResult = await joinCommunity({
              inviteCode: code,
              doInfoChecks: false,
              joinRequirementsResponses: responses ?? undefined,
            })
            const fallbackData = fallbackResult.joinCommunityWithInfoCheck

            if (fallbackData?.requestToJoinSubmitted) {
              if (fallbackData.communityToJoin) {
                setCommunityToJoin(fallbackData.communityToJoin as BaseCommunityFragment)
              }
              setStep("submitted")
              return
            }

            if (fallbackData?.communities) {
              setCommunities(fallbackData.communities as BaseCommunityFragment[])
              return
            }

            if (fallbackData?.joinRequirements) {
              setJoinRequirementsConfig(
                fallbackData.joinRequirements as { fields: JoinRequirementFieldConfig[] },
              )
              if (fallbackData.communityToJoin) {
                setCommunityToJoin(fallbackData.communityToJoin as BaseCommunityFragment)
              }
              setStep("requirements")
              return
            }

            setError("Something went wrong, please try again.")
            return
          }

          setRequiredFields(supportedAccountFields)
          if (data.communityToJoin) {
            setCommunityToJoin(data.communityToJoin as BaseCommunityFragment)
          }
          setStep("account")
          return
        }

        if (data?.joinRequirements) {
          setJoinRequirementsConfig(
            data.joinRequirements as { fields: JoinRequirementFieldConfig[] },
          )
          if (data.communityToJoin) {
            setCommunityToJoin(data.communityToJoin as BaseCommunityFragment)
          }
          setStep("requirements")
          return
        }
      } catch (err) {
        processException(err)
        // A link's code that finds nothing, in a community without its own code: retrying won't help.
        const linkCodeFailed =
          getGraphQLErrorMessage(err) === INVITE_CODE_NOT_FOUND &&
          !linkCodeUsedRef.current &&
          !!normalizeInviteCode(inviteCode)
        setError(
          linkCodeFailed
            ? "This invite link doesn't work anymore. Ask for a new one."
            : "Something went wrong, please try again.",
        )
      } finally {
        setIsLoading(false)
      }
    },
    [joinCommunity, getInviteCode, setCommunities, customizationData?.inviteCode, inviteCode],
  )

  const handleJoinClick = useCallback(async () => {
    // Communities where joining requires a purchase: the checkout creates the join request, so
    // send the visitor to the listed ticket's event and never call the join mutation here.
    const joinPurchasePath = await resolveJoinPurchasePath(
      community,
      typeof router.query.code === "string" ? router.query.code : undefined,
    )
    if (joinPurchasePath) {
      void router.push(joinPurchasePath)
      return
    }

    if (!getInviteCode()) {
      setError("This community is not accepting new members yet.")
      return
    }

    if (!user) {
      setStep("auth")
    } else {
      void performJoinCheck()
    }
  }, [community, getInviteCode, performJoinCheck, router, user])

  const handleAuthSuccess = useCallback(
    async (params: { accessToken: string; user: BaseUserFragment | undefined }) => {
      setAccessToken(params.accessToken)
      setUser(params.user)
      await performJoinCheck(undefined)
    },
    [setAccessToken, setUser, performJoinCheck],
  )

  const handleAccountComplete = useCallback(async () => {
    await performJoinCheck()
    await refreshUser().catch(() => undefined)
  }, [performJoinCheck, refreshUser])

  const handleRequirementsSubmit = useCallback(
    async (responses: Record<string, unknown>) => {
      await performJoinCheck(responses)
    },
    [performJoinCheck],
  )

  if (!mounted || !customizationData || !isOpen) return null
  const mobileOnly = !!customizationData.styles.global.mobileOnly

  return (
    <>
      {step === "gate" && (
        <Landing
          error={error}
          isLoading={isLoading}
          modalContainerStyles={GATE_MODAL_CONTAINER_STYLES}
          modalPanelStyles={GATE_MODAL_PANEL_STYLES}
          modalViewportStyles={GATE_MODAL_VIEWPORT_STYLES}
          onJoinClick={handleJoinClick}
        />
      )}

      {!mobileOnly && (
        <>
          {step === "auth" && (
            <Auth
              modalContainerStyles={GATE_MODAL_CONTAINER_STYLES}
              modalPanelStyles={GATE_MODAL_PANEL_STYLES}
              modalViewportStyles={GATE_MODAL_VIEWPORT_STYLES}
              onAuthSuccess={handleAuthSuccess}
              onBack={() => setStep("gate")}
            />
          )}

          {step === "account" && (
            <CompleteAccount
              modalContainerStyles={GATE_MODAL_CONTAINER_STYLES}
              modalPanelStyles={GATE_MODAL_PANEL_STYLES}
              modalViewportStyles={GATE_MODAL_VIEWPORT_STYLES}
              onBack={() => {
                setStep("gate")
                setError(null)
              }}
              onComplete={handleAccountComplete}
              requiredFields={requiredFields}
            />
          )}

          {step === "requirements" && (
            <Requirements
              communityToJoin={communityToJoin}
              error={error}
              isSubmitting={isLoading}
              joinRequirementsConfig={joinRequirementsConfig}
              modalContainerStyles={GATE_MODAL_CONTAINER_STYLES}
              modalPanelStyles={GATE_MODAL_PANEL_STYLES}
              modalViewportStyles={GATE_MODAL_VIEWPORT_STYLES}
              onSubmit={handleRequirementsSubmit}
            />
          )}

          {step === "submitted" && (
            <Submitted
              communityToJoin={communityToJoin}
              modalContainerStyles={GATE_MODAL_CONTAINER_STYLES}
              modalPanelStyles={GATE_MODAL_PANEL_STYLES}
              modalViewportStyles={GATE_MODAL_VIEWPORT_STYLES}
              onLogout={logout}
            />
          )}
        </>
      )}
    </>
  )
}
